import React from "react";
import { Redirect } from "wouter";
import { useGetMe } from "@workspace/api-client-react";
import { Loader2 } from "lucide-react";

/**
 * Outermost access gate for every authenticated surface. The backend is the
 * source of truth (domain routers are gated by requireActivated /
 * requireCompletedProfile); this keeps the UX consistent by routing users to
 * the right full-screen step before they can reach the app shell:
 *   - not activated            -> /activate  (dual-channel OTP)
 *   - activated, profile due   -> /complete-profile (internal staff / AM)
 * Agents and master dealers are profile-complete by definition, so they fall
 * straight through (agents are then handled by AgentOnboardingGate).
 *
 * Fails closed: while `me` is loading, or if a derived flag is missing, the
 * user stays gated rather than slipping through to the app.
 */
export function AccountActivationGate({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: me, isLoading } = useGetMe();

  if (isLoading || !me) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-gray-50 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  if (me.activated !== true) {
    return <Redirect to="/activate" />;
  }
  if (me.profileComplete !== true) {
    return <Redirect to="/complete-profile" />;
  }

  return <>{children}</>;
}
